import { Component } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { UtilDialogService } from '../../services/dialog.service';

export interface OrderIssue {
  message: string;
  details?: string[];
}

export interface OrderIssuesDialogData {
  issues: OrderIssue[];
  accountUserId?: string;
  dialogService?: UtilDialogService;
}

@Component({
  selector: 'prox-order-issues-dialog',
  template: `
    <div class="order-issues-container">
      <div *ngFor="let issue of data.issues; let i = index" class="issue-card">
        <div class="issue-message">
          <span class="issue-bullet">&bull;</span>
          <span [innerHTML]="issue.message"></span>
        </div>
        <div *ngIf="issue.details && issue.details.length > 0" class="issue-details">
          <div class="details-header">Affected Program User Groups:</div>
          <ul class="details-list">
            <li *ngFor="let detail of issue.details">{{ detail }}</li>
          </ul>
        </div>
      </div>

      <div *ngIf="canContactAdmin && !sendingEmail && !emailSent" class="contact-footer">
        <strong>Click the 'Contact Administrator' button below to resolve this issue.</strong>
      </div>

      <div *ngIf="emailSent" class="email-confirmation">
        <i class="pi pi-check-circle"></i>
        E-mails have been successfully sent to {{ emailCount }} administrator(s).
      </div>

      <div class="button-row">
        <button (click)="close()" pButton type="button" icon="pi pi-times" label="Close" class="p-button-raised"></button>
        <button
          *ngIf="canContactAdmin && !emailSent"
          (click)="contactAdmin()"
          pButton
          type="button"
          icon="pi pi-envelope"
          label="Contact Administrator"
          class="p-button-raised p-button-secondary"
          [disabled]="sendingEmail"
          [loading]="sendingEmail">
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      .order-issues-container {
        font-size: 14px;
        line-height: 1.6;
      }

      .issue-card {
        margin-bottom: 16px;
        padding: 12px;
        background-color: #fef3c7;
        border-left: 4px solid #f59e0b;
        border-radius: 4px;
      }

      .issue-message {
        font-weight: 500;
        margin-bottom: 4px;
      }

      .issue-bullet {
        margin-right: 6px;
      }

      .issue-details {
        margin-left: 16px;
        margin-top: 8px;
        padding: 8px;
        background-color: #fff;
        border-radius: 4px;
      }

      .details-header {
        font-size: 12px;
        color: #666;
        margin-bottom: 4px;
      }

      .details-list {
        margin: 0;
        padding-left: 20px;
        list-style-type: disc;
      }

      .details-list li {
        margin-bottom: 2px;
        color: #333;
      }

      .contact-footer {
        margin-top: 16px;
        padding: 12px 0;
        text-align: center;
        font-size: 15px;
      }

      .email-confirmation {
        margin-top: 12px;
        padding: 12px;
        background-color: #d1fae5;
        border-left: 4px solid #10b981;
        border-radius: 4px;
        color: #065f46;
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .email-confirmation i {
        font-size: 1.2rem;
      }

      .button-row {
        margin-top: 16px;
        display: flex;
        gap: 8px;
      }
    `,
  ],
  standalone: false,
})
export class OrderIssuesDialogComponent {
  data: OrderIssuesDialogData;
  sendingEmail = false;
  emailSent = false;
  emailCount = 0;

  constructor(
    dialogConfig: DynamicDialogConfig,
    private ref: DynamicDialogRef,
  ) {
    this.data = dialogConfig.data;
  }

  get canContactAdmin(): boolean {
    return !!(this.data.accountUserId && this.data.dialogService);
  }

  close() {
    this.ref.close();
  }

  async contactAdmin() {
    if (!this.data.accountUserId || !this.data.dialogService) {
      return;
    }

    this.sendingEmail = true;
    try {
      const response = await this.data.dialogService.contactAdminsAboutOrderIssue(
        this.data.accountUserId,
        this.data.issues
      );

      this.emailSent = true;
      this.emailCount = response.emailsSent;
    } catch (error) {
      this.emailSent = false;
    } finally {
      this.sendingEmail = false;
    }
  }
}
